import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Pages
import Home from "./pages/Home";
import Courses from "./pages/Courses";
import About from "./pages/About";
import Results from "./pages/Results";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import Register from "./pages/Register";
import Payment from "./pages/Payment";
import PaymentSuccess from "./pages/PaymentSuccess";
import PaymentFailed from "./pages/PaymentFailed";
import StudentPortal from "./pages/StudentPortal";
import StudentLogin from "./pages/StudentLogin";
import StudentDashboard from "./pages/StudentDashboard";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import CourseDetail from "./pages/CourseDetail";
import ComplimentarySession from "./pages/ComplimentarySession";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/AdminDashboard";
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = BACKEND_URL + "/api";

// Safe localStorage wrapper for Safari private mode
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.warn('Storage not available');
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.warn('Storage not available');
    }
  }
};

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      const token = safeStorage.getItem("adminToken");
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(API + "/admin/me", {
          headers: { Authorization: "Bearer " + token }
        });
        setAdmin(res.data);
      } catch (error) {
        // Token invalid or expired - clear it
        console.log("Token validation failed, clearing session");
        safeStorage.removeItem("adminToken");
        setAdmin(null);
      } finally {
        setLoading(false);
      }
    };
    
    validateToken();
  }, []);

  const login = useCallback(async function(email, password) {
    const res = await axios.post(API + "/admin/login", { email: email, password: password });
    safeStorage.setItem("adminToken", res.data.token);
    setAdmin(res.data.admin);
    return res.data;
  }, []);

  const logout = useCallback(function() {
    safeStorage.removeItem("adminToken");
    setAdmin(null);
    // Force redirect handled by ProtectedRoute
  }, []);

  return (
    <AuthContext.Provider value={{ admin: admin, login: login, logout: logout, loading: loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route - Blocks access to admin pages without authentication
const ProtectedRoute = function({ children }) {
  const { admin, loading } = useAuth();
  const token = safeStorage.getItem("adminToken");
  
  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-deep-blue">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }
  
  // No token or no admin = redirect to login immediately
  if (!token || !admin) {
    // Clear any stale tokens
    safeStorage.removeItem("adminToken");
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Layout><Home /></Layout>} />
          <Route path="/courses" element={<Layout><Courses /></Layout>} />
          <Route path="/courses/:courseId" element={<Layout><CourseDetail /></Layout>} />
          <Route path="/about" element={<Layout><About /></Layout>} />
          <Route path="/results" element={<Layout><Results /></Layout>} />
          <Route path="/blog" element={<Layout><Blog /></Layout>} />
          <Route path="/blog/:slug" element={<Layout><BlogPost /></Layout>} />
          <Route path="/faq" element={<Layout><FAQ /></Layout>} />
          <Route path="/contact" element={<Layout><Contact /></Layout>} />
          <Route path="/register" element={<Layout><Register /></Layout>} />
          <Route path="/complimentary-session" element={<Layout><ComplimentarySession /></Layout>} />
          <Route path="/payment" element={<Layout><Payment /></Layout>} />
          <Route path="/payment-success" element={<Layout><PaymentSuccess /></Layout>} />
          <Route path="/payment-failed" element={<Layout><PaymentFailed /></Layout>} />
          <Route path="/student-portal" element={<Layout><StudentPortal /></Layout>} />
          <Route path="/student/login" element={<StudentLogin />} />
          <Route path="/student/dashboard" element={<StudentDashboard />} />
          <Route path="/terms" element={<Layout><Terms /></Layout>} />
          <Route path="/privacy" element={<Layout><Privacy /></Layout>} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/*" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
